import { useMemo } from 'react';
import { Box } from '@mui/material';
import type { RockTCSConformityReading, RockTCSGeneral, RockTCSUltrasonicReading } from '../../../types/rockTcs';
import {
  buildShapeToleranceStatement,
  computeConformityPreview,
  computeSpecimenPreview,
} from './computations';
import { SpecimenParametersSection } from './sections/SpecimenParametersSection';
import { ConformitySection } from './sections/ConformitySection';
import { UltrasonicSection } from './sections/UltrasonicSection';
import { CompressionSetupSection } from './sections/CompressionSetupSection';

interface Props {
  general: RockTCSGeneral;
  conformity: RockTCSConformityReading[];
  ultrasonic: RockTCSUltrasonicReading[];
  onChange: (field: keyof RockTCSGeneral, value: RockTCSGeneral[keyof RockTCSGeneral]) => void;
  onConformityChange: (rows: RockTCSConformityReading[]) => void;
  onUltrasonicChange: (rows: RockTCSUltrasonicReading[]) => void;
  readOnly?: boolean;
}

/**
 * Rock TCS Test Parameters tab — four sections: Specimen Parameters (now
 * hosting Specimen Condition), Conformity & ASTM Shape Tolerance, Ultrasonic
 * Velocity, and Compression Test Setup. All derived values preview live via
 * computations.ts (the backend recomputes authoritatively on save).
 *
 * BUG-2026-0137 §1–§3: the "Test / Sample Summary" section is gone — the Rock
 * Description and Preparation boxes were removed from the sheet (the DB
 * columns remain; existing saved values are preserved untouched) and Specimen
 * Condition moved into Specimen Parameters.
 */
export const RockTCSParametersTab = ({
  general, conformity, ultrasonic, onChange, onConformityChange, onUltrasonicChange, readOnly,
}: Props) => {
  const specimenPreview = useMemo(() => computeSpecimenPreview({
    diameters: [general.diameter1, general.diameter2, general.diameter3, general.diameter4, general.diameter5, general.diameter6],
    lengths: [general.length1, general.length2, general.length3, general.length4],
    massG: general.massG,
    gaugeLengths: [general.gaugeLength1, general.gaugeLength2, general.gaugeLength3],
  }), [general]);

  const conformityPreview = useMemo(() => {
    const series = [] as { endNumber: number; diameterLine: number; points: { positionMm: number; valueMm: number | null }[] }[];
    for (const endNumber of [1, 2]) {
      for (const diameterLine of [1, 2]) {
        const points = conformity
          .filter((r) => r.endNumber === endNumber && r.diameterLine === diameterLine)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((r) => ({ positionMm: r.positionMm, valueMm: r.valueMm }));
        if (points.length > 0) series.push({ endNumber, diameterLine, points });
      }
    }
    return computeConformityPreview({
      series,
      sideStraightnessMm: general.sideStraightnessMm,
      lOverD: specimenPreview.lOverD,
      lOverDPass: specimenPreview.lOverDPass,
    });
  }, [conformity, general.sideStraightnessMm, specimenPreview.lOverD, specimenPreview.lOverDPass]);

  const shapeStatement = useMemo(
    () => buildShapeToleranceStatement(conformityPreview.failReasons, general.conformityFailReason),
    [conformityPreview.failReasons, general.conformityFailReason],
  );

  return (
    <Box sx={{ display: 'grid', rowGap: 1.5, pb: 1 }}>
      {/* ── 1. Specimen Parameters (hosts Specimen Condition — §3) ── */}
      <SpecimenParametersSection
        general={general}
        preview={specimenPreview}
        onChange={onChange}
        readOnly={readOnly}
      />

      {/* ── 2. Conformity & ASTM Shape Tolerance ── */}
      <ConformitySection
        general={general}
        conformity={conformity}
        preview={conformityPreview}
        lOverD={specimenPreview.lOverD}
        shapeStatement={shapeStatement}
        onChange={onChange}
        onConformityChange={onConformityChange}
        readOnly={readOnly}
      />

      {/* ── 3. Ultrasonic Velocity ── */}
      <UltrasonicSection
        general={general}
        ultrasonic={ultrasonic}
        onChange={onChange}
        onUltrasonicChange={onUltrasonicChange}
        readOnly={readOnly}
      />

      {/* ── 4. Compression Test Setup ── */}
      <CompressionSetupSection
        general={general}
        avgGaugeLengthMm={specimenPreview.avgGaugeLengthMm}
        onChange={onChange}
        readOnly={readOnly}
      />
    </Box>
  );
};
